import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Text,
  useColorModeValue,
  Flex,
  Skeleton,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  SimpleGrid,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { GiVillage } from 'react-icons/gi';
import { FaCity } from 'react-icons/fa';
import { useRegionData } from '../../../../contexts/RegionDataContext';

const Mandal = ({ onCountUpdate }) => {
  const [searchInput, setSearchInput] = useState('');
  const { circles, isLoading, searchCircles, refreshCircles, counts } =
    useRegionData();

  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const gradientStart = useColorModeValue('orange.400', 'orange.600');
  const gradientEnd = useColorModeValue('red.500', 'red.600');

  const filteredCircles = useMemo(() => {
    if (!searchInput) return circles;
    return circles.filter((c) =>
      c.name?.toLowerCase().includes(searchInput.toLowerCase()),
    );
  }, [circles, searchInput]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value) {
      searchCircles(value);
    }
  };

  const handleRefresh = () => {
    refreshCircles();
    setSearchInput('');
  };

  useEffect(() => {
    if (onCountUpdate && counts.mandal > 0) {
      onCountUpdate(counts.mandal);
    }
  }, [counts.mandal, onCountUpdate]);

  return (
    <Box position="relative">
      <Card
        mb={4}
        borderRadius="lg"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="6px"
          bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd})`}
        />

        <CardBody p={4}>
          <Flex align="center" gap={4} mb={6} pt={2}>
            <Box
              p={3}
              borderRadius="xl"
              bg="orange.100"
              _dark={{ bg: 'orange.900' }}
              position="relative"
              overflow="hidden"
            >
              <Icon as={GiVillage} boxSize={6} color="orange.500" />
            </Box>
            <VStack align="flex-start" spacing={1}>
              <Heading size="md" color={textColor}>
                Circles (Mandals)
              </Heading>
              <Text fontSize="sm" color={subtitleColor}>
                {counts.mandal} administrative circles
              </Text>
            </VStack>
          </Flex>

          <Flex
            mb={6}
            gap={4}
            wrap="wrap"
            align="center"
            justify="space-between"
          >
            <InputGroup size="md" flex="1" minW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="orange.500" />
              </InputLeftElement>
              <Input
                placeholder="Search circles..."
                value={searchInput}
                onChange={handleSearchChange}
                color={textColor}
                borderRadius="lg"
                border="2px solid"
                borderColor={borderColor}
                _focus={{
                  borderColor: 'orange.500',
                  boxShadow: '0 0 0 1px rgba(237, 137, 54, 0.6)',
                }}
                _hover={{
                  borderColor: 'orange.300',
                }}
              />
            </InputGroup>

            <Flex gap={3} align="center">
              <Badge
                colorScheme="orange"
                variant="subtle"
                px={4}
                py={2}
                borderRadius="full"
              >
                {filteredCircles.length} items
              </Badge>
            </Flex>
          </Flex>

          <Box height={'60vh'} overflow={'auto'}>
            {isLoading.circles ? (
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={4} p={6}>
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} height="140px" borderRadius="lg" />
                ))}
              </SimpleGrid>
            ) : filteredCircles.length === 0 ? (
              <VStack
                spacing={4}
                p={8}
                textAlign="center"
                borderRadius="lg"
                border="2px dashed"
                borderColor={borderColor}
              >
                <Icon as={GiVillage} boxSize={12} color="gray.400" />
                <Text color={subtitleColor} fontSize="lg" fontWeight="medium">
                  {circles.length === 0
                    ? 'No circles found'
                    : 'No matching circles found'}
                </Text>
                <Text color={subtitleColor} fontSize="sm">
                  {circles.length === 0
                    ? 'Start by adding some circles'
                    : 'Try a different search term'}
                </Text>
              </VStack>
            ) : (
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={4}>
                {filteredCircles.map((circle, index) => (
                  <Card
                    key={circle._id || index}
                    borderRadius="lg"
                    bg={cardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    _hover={{
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s',
                    }}
                  >
                    <CardBody p={4}>
                      <VStack spacing={3} align="stretch">
                        <Flex align="center" gap={3}>
                          <Box
                            p={2}
                            borderRadius="lg"
                            bg="orange.50"
                            _dark={{ bg: 'orange.900' }}
                          >
                            <Icon
                              as={GiVillage}
                              boxSize={5}
                              color="orange.500"
                            />
                          </Box>
                          <Box flex="1">
                            <Text
                              fontWeight="bold"
                              color={textColor}
                              fontSize="md"
                              noOfLines={1}
                            >
                              {circle.name?.toUpperCase() || 'N/A'}
                            </Text>
                            <Text fontSize="xs" color={subtitleColor}>
                              Circle (Mandal)
                            </Text>
                          </Box>
                        </Flex>

                        {/* Tahsil Info */}
                        {circle.tahsil?.name && (
                          <Flex align="center" gap={2}>
                            <Icon as={FaCity} color="blue.500" boxSize={4} />
                            <Box flex="1">
                              <Text fontSize="xs" color={subtitleColor}>
                                Tahsil
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color={textColor}
                              >
                                {circle.tahsil.name}
                              </Text>
                            </Box>
                          </Flex>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Box>

          <Flex
            justify="space-between"
            align="center"
            mt={6}
            pt={4}
            borderTop="1px solid"
            borderColor={borderColor}
          >
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="sm" color={subtitleColor}>
                Showing <strong>{filteredCircles.length}</strong> of{' '}
                <strong>{circles.length}</strong> circles
              </Text>
            </VStack>
            <Text fontSize="xs" color={subtitleColor}>
              Updated:{' '}
              {new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </Flex>
        </CardBody>
      </Card>
    </Box>
  );
};

export default React.memo(Mandal);
